import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useConfirm } from '@/hooks/useConfirm';
import { jobFamilySyncService, OrgStructureNode } from '@/services/api/organizationService';

const ReassignJobsDialog: React.FC<{
  sourceFunction: OrgStructureNode;
  allFunctions: OrgStructureNode[];
  onClose: () => void;
}> = ({ sourceFunction, allFunctions, onClose }) => {
  const { confirm: showConfirm } = useConfirm();
  const [preview, setPreview] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [targetFamily, setTargetFamily] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadPreview();
  }, []);

  const loadPreview = async () => {
    setIsLoading(true);
    try {
      // Use the merge preview endpoint with a dummy target just to get the job list
      const data = await jobFamilySyncService.mergeFunctions({
        source_job_family_ids: [sourceFunction.code],
        target_job_family_id: sourceFunction.code, // same as source — preview only, won't execute
        preview: true,
      });
      setPreview(data);
      setSelectedJobs(new Set((data.jobs_affected || []).map((j: any) => j.job_id)));
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecute = async () => {
    if (selectedJobs.size === 0 || !targetFamily) return;
    const confirmed = await showConfirm(
      `Reassign ${selectedJobs.size} Jobs from "${sourceFunction.name}" to "${targetFamily}"?\n\nThis cannot be automatically undone.`,
      'Reassign Jobs',
      { confirmText: `Reassign ${selectedJobs.size} Jobs`, type: 'warning' }
    );
    if (!confirmed) return;

    setIsExecuting(true);
    try {
      const res = await jobFamilySyncService.reassignJobs({
        job_ids: Array.from(selectedJobs),
        source_job_family_id: sourceFunction.code,
        target_job_family_id: targetFamily,
        preview: false,
      });
      setResult(res);
    } catch (error) {
      console.error('Reassign failed:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const [hideNoPositions, setHideNoPositions] = useState(false);
  const allJobs: any[] = preview?.jobs_affected || [];
  const jobs = hideNoPositions ? allJobs.filter((j: any) => (j.positions_count ?? 0) > 0) : allJobs;

  if (result) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Reassignment Complete</DialogTitle>
        </DialogHeader>
        <div className="bg-emerald-50 rounded p-4 text-center">
          <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
          <p className="font-medium">{result.jobs_updated} Jobs reassigned to {targetFamily}</p>
          {result.jobs_failed > 0 && <p className="text-red-600 text-sm">{result.jobs_failed} failed</p>}
        </div>
        <DialogFooter><Button onClick={onClose}>Done</Button></DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Reassign Jobs from "{sourceFunction.name}"</DialogTitle>
        <DialogDescription>
          Select Jobs to move to a different Function. {jobs.length} Jobs{hideNoPositions ? ` (${allJobs.length - jobs.length} with no positions hidden)` : ` currently in this family`}.
        </DialogDescription>
      </DialogHeader>

      {isLoading ? (
        <div className="py-8 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading Jobs...</div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2">Target Function:</label>
            <Select value={targetFamily} onValueChange={setTargetFamily}>
              <SelectTrigger><SelectValue placeholder="Select target Function..." /></SelectTrigger>
              <SelectContent>
                {allFunctions.filter(f => f.code !== sourceFunction.code).map(fn => (
                  <SelectItem key={fn.code} value={fn.code}>{fn.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-3 text-xs">
            <button className="text-blue-600 hover:underline" onClick={() => setSelectedJobs(new Set(jobs.map((j: any) => j.job_id)))}>
              Select all ({jobs.length})
            </button>
            <button className="text-blue-600 hover:underline" onClick={() => setSelectedJobs(new Set())}>
              Deselect all
            </button>
            <span className="text-gray-500">{selectedJobs.size} of {jobs.length} selected</span>
            <span className="text-gray-300">|</span>
            <label className="flex items-center gap-1 cursor-pointer text-gray-500">
              <input type="checkbox" checked={hideNoPositions} onChange={e => setHideNoPositions(e.target.checked)} className="rounded border-gray-300" />
              Hide jobs with no positions
              {hideNoPositions && <span className="text-gray-400">({allJobs.length - jobs.length} hidden)</span>}
            </label>
          </div>

          <div className="max-h-[45vh] overflow-y-auto border rounded">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs w-8"></TableHead>
                <TableHead className="text-xs">Job</TableHead>
                <TableHead className="text-xs">Title</TableHead>
                <TableHead className="text-xs">Description</TableHead>
                <TableHead className="text-xs text-right">Positions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {jobs.map((job: any) => (
                  <TableRow key={job.job_id} className={selectedJobs.has(job.job_id) ? '' : 'opacity-40'}>
                    <TableCell className="py-1 px-2">
                      <input type="checkbox" checked={selectedJobs.has(job.job_id)} className="rounded"
                        onChange={e => {
                          const next = new Set(selectedJobs);
                          e.target.checked ? next.add(job.job_id) : next.delete(job.job_id);
                          setSelectedJobs(next);
                        }} />
                    </TableCell>
                    <TableCell className="text-xs py-1">
                      <div className="font-medium">{job.title_id || job.description || job.job_id}</div>
                      <div className="text-[10px] font-mono text-gray-400">{job.job_id}</div>
                    </TableCell>
                    <TableCell className="text-xs py-1">{job.title_id}</TableCell>
                    <TableCell className="text-xs py-1 text-gray-500">{job.description}</TableCell>
                    <TableCell className="text-xs py-1 text-right">{job.positions_count || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleExecute} disabled={isExecuting || selectedJobs.size === 0 || !targetFamily}
          className="bg-indigo-600 hover:bg-indigo-700">
          {isExecuting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Reassign {selectedJobs.size} Jobs
        </Button>
      </DialogFooter>
    </>
  );
};

export default ReassignJobsDialog;
